import { memo, useEffect, useState } from 'react'
import type { InViewHookResponse } from 'react-intersection-observer'

import { fetchMetasearchDiscount } from 'actions/MetasearchDiscountActions'
import { trackEvent } from 'analytics/analytics'
import {
  offerPageDurationChangeEvent,
  offerPageGuestSelectionEvent,
  offerPageHotLeadEvent,
} from 'analytics/snowplow/events'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import OfferPageAssistantHost from 'tripPlanner/assistant/surfaces/offerPage/OfferPageAssistantHost'
import { getNextFilterWithCapacityCheckInMiddleware } from 'components/OfferPage/utils'
import { Experiments } from 'constants/experiments'
import { FORCED_SPP_BEDBANK_OFFER_IDS } from 'constants/property'
import OfferPageStateProvider from 'contexts/OfferPage/offerPageStateProvider'
import type { OfferPageState, OfferPageDispatchAction } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions, generateOfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageReducer from 'hooks/OfferPage/useOfferPageReducer'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useSaveHotLead } from 'hooks/Recommendation/useSaveHotLead'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import useMetasearchPriceId from 'hooks/useMetasearchPriceId'
import useMountedEffect from 'hooks/useMountedEffect'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import {
  getOptimizelyEnabledDecision,
  getOptimizelyExperimentVariation,
} from 'lib/optimizely/optimizelyUtils'
import type { ReducerMiddleware } from 'lib/redux/reducerUtils'
import { serialiseOccupancy } from 'lib/search/searchUtils'
import { getInitialPropertyPageState } from 'selectors/offerPage/initialOfferPageStateSelectors'
import { getPropertyCheapestHotelOnlyRateForDiscount } from 'selectors/offerPage/offerPageSelectors'
import { useSyncBedbankTripItem } from 'tripPlanner/hooks/bookmarks/useSyncBedbankTripItem'
import { useSyncHotelTripItem } from 'tripPlanner/hooks/bookmarks/useSyncHotelTripItem'

import PropertyContent from './PropertyContent'
import propertyMobileFilterMiddleware from './PropertyMobileFilterMiddleware'

const mobileVariantMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [propertyMobileFilterMiddleware]
const defaultMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [getNextFilterWithCapacityCheckInMiddleware]
const defaultRooms = [generateOfferPageRoom()]

interface Props {
  property: App.Property.Property
  /**
   * @deprecated Use `property` instead. (SPP combines multiple offers into a single property; there
   * is no canonical offer.)
   */
  offer: App.HotelOffer | App.BedbankOffer
  shouldUseLereStickyFooter: boolean
  lereYmalStickyFooterShowTrigger: InViewHookResponse
}

function PropertyContainer(props: Props) {
  const { property, offer, shouldUseLereStickyFooter, lereYmalStickyFooterShowTrigger } = props
  const [lpcOffer, bedbankOffer] = isBedbankOffer(offer) ? [undefined, offer] : [offer, undefined]
  const [middlewares, setMiddlewares] = useState(defaultMiddlewares)
  const dispatch = useAppDispatch()
  const defaults = useAppSelector(state => getInitialPropertyPageState(state, lpcOffer, bedbankOffer, property))

  const isMobileDevice = useIsMobileDevice()
  const [state, stateDispatch] = useOfferPageReducer({
    ...defaults,
    duration: defaults.duration ?? 1,
    offer: lpcOffer,
    bedbankOffer,
  }, middlewares)

  const cheapestHotelOnlyRate = useAppSelector(appState => getPropertyCheapestHotelOnlyRateForDiscount(state, appState))
  const discountToken = useMetasearchPriceId()

  useSyncHotelTripItem(state, lpcOffer)
  useSyncBedbankTripItem(state, bedbankOffer)
  useExperiment(Experiments.croLppCustomContentV2, { canActivate: property.hasContentVariant })
  const { isAnyVariant: isNewMobileBookingFlowEnabled } = useExperiment(
    Experiments.croAlwaysOnNewMobileBooking,
    { canActivate: isMobileDevice },
  )

  const useNewMobileBookingFlow = isMobileDevice && isNewMobileBookingFlowEnabled

  useEffect(() => {
    if (useNewMobileBookingFlow) {
      setMiddlewares(mobileVariantMiddlewares)
    } else {
      setMiddlewares(defaultMiddlewares)
    }
  }, [useNewMobileBookingFlow])

  useEffect(() => {
    stateDispatch({ type: OfferPageActions.setProperty, property })
  }, [stateDispatch, property])

  useMountedEffect(() => {
    // the first loaded offer from an SSR is a summary. We then fetch the full version
    // This ensures the offer page state has our latest version
    if (lpcOffer) stateDispatch({ type: OfferPageActions.setOffer, offer: lpcOffer })
    if (bedbankOffer) stateDispatch({ type: OfferPageActions.setBedBankOffer, bedbankOffer })
  }, [lpcOffer, bedbankOffer])

  useMountedEffect(() => {
    trackEvent(offerPageDurationChangeEvent(offer, state))
  }, [state.duration])

  useMountedEffect(() => {
    if (state.rooms) {
      trackEvent(offerPageGuestSelectionEvent(offer, state))
    }
  }, [state.rooms])

  const hotLead = useSaveHotLead({
    offerId: offer.id,
    offerType: offer.type,
    checkInDate: state.checkInDate,
    duration: state.duration,
    occupancy: state.rooms,
    defaultOccupancy: defaultRooms,
  })

  useEffect(() => {
    if (hotLead?.checkIn && hotLead.checkOut) {
      trackEvent(offerPageHotLeadEvent(offer, state))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [hotLead])

  useEffect(() => {
    if (
      discountToken &&
      cheapestHotelOnlyRate &&
      'service' in cheapestHotelOnlyRate &&
      cheapestHotelOnlyRate.service.type === 'bedbank' &&
      state.checkInDate &&
      state.checkOutDate
    ) {
      dispatch(fetchMetasearchDiscount({
        token: discountToken,
        propertyId: cheapestHotelOnlyRate.service.propertyId,
        checkIn: state.checkInDate,
        checkOut: state.checkOutDate,
        price: cheapestHotelOnlyRate.totals.displayPrice,
        propertyFees: cheapestHotelOnlyRate.totals.propertyFees,
        occupancy: serialiseOccupancy(state.rooms),
        mx: cheapestHotelOnlyRate.totals.mx,
      }))
    }
  }, [cheapestHotelOnlyRate, discountToken, dispatch, state.checkInDate, state.checkOutDate, state.rooms])

  /* -------- START of treatment for SPP for bedbank properties with promotions A/B test -------- */

  const hasBedbankPromotions = !!bedbankOffer?.promotions.length && !!property.promotion?.endIsoDateTime
  const isForcedSpp = !!bedbankOffer?.id && FORCED_SPP_BEDBANK_OFFER_IDS.has(bedbankOffer.id)
  const isTreatmentSppBedbankPromosVariation = useAppSelector(state => getOptimizelyEnabledDecision(
    getOptimizelyExperimentVariation(state, Experiments.sppBedbankPromos.id),
  ))
  useExperiment(Experiments.sppBedbankPromos, {
    canActivate: hasBedbankPromotions && !isForcedSpp && isTreatmentSppBedbankPromosVariation,
  })

  /* --------- END of treatment for SPP for bedbank properties with promotions A/B test --------- */

  // Ensure descendants can depend on `offerPageState.property` being set.
  if (!state.property) return <LoadingIndicator />

  return <OfferPageStateProvider state={state} dispatch={stateDispatch}>
    <OfferPageAssistantHost offerPageState={state}>
      <PropertyContent
        property={property}
        offer={offer as App.HotelOffer /* TODO(HP-1495): Remove dependency. */}
        shouldUseLereStickyFooter={shouldUseLereStickyFooter}
        lereYmalStickyFooterShowTrigger={lereYmalStickyFooterShowTrigger}
      />
    </OfferPageAssistantHost>
  </OfferPageStateProvider>
}

export default memo(PropertyContainer)
